/**
 * CustomerRouteGuard
 *
 * Guards customer-only surfaces (/accounts/me, /accounts/details, /accounts/billing).
 * Closes bug 1AC21B's "split brain" — a user with `roles = ['CUSTOMER', 'COMPLIANCE']`
 * was rendering Overview while the Shell sidebar (which picks the highest-priority
 * role) showed compliance nav.
 *
 * The guard mirrors Shell.actualRole's semantics: highest-priority role wins.
 * If the *effective* role is a non-admin staff role, the user is acting as
 * staff right now — they don't render the customer Overview, even if CUSTOMER
 * happens to be in their roles array.
 *
 * Access rules:
 *   1. Effective role is CUSTOMER → allow.
 *   2. Effective role is ADMIN / SUPER_ADMIN → allow (no opt-in needed).
 *   3. Effective role is staff (CM/COMPLIANCE/OPS) AND
 *      `shell-view-as-customer === 'true'` in sessionStorage → allow.
 *      The toggle is the explicit opt-in signal from Shell.tsx; without
 *      it, a staff user landing here got here by accident (stale link,
 *      remaining routing bug) and must bounce.
 *   4. Otherwise → bounce to /dashboard, which then routes the user to
 *      their role-appropriate dashboard.
 */
import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '@shared/context';

const VIEW_AS_CUSTOMER_KEY = 'shell-view-as-customer';

// Mirrors Shell.tsx ROLE_PRIORITY ordering. Kept local to avoid coupling
// portal/src/routes to @shared/components/Shell internals.
const ROLE_PRIORITY: Record<string, number> = {
  CUSTOMER: 1,
  OPERATIONS: 2,
  CASE_MANAGER: 2,
  COMPLIANCE: 2,
  ADMIN: 3,
  SUPER_ADMIN: 4,
};

export function getEffectiveRole(roles: readonly string[]): string {
  if (!roles.length) return 'CUSTOMER';
  let best = 'CUSTOMER';
  let bestPriority = 0;
  for (const role of roles) {
    const p = ROLE_PRIORITY[role] ?? 0;
    if (p > bestPriority) {
      bestPriority = p;
      best = role;
    }
  }
  return best;
}

function isViewingAsCustomer(): boolean {
  try {
    return sessionStorage.getItem(VIEW_AS_CUSTOMER_KEY) === 'true';
  } catch {
    return false;
  }
}

export interface CustomerRouteGuardProps {
  children: React.ReactNode;
}

export function CustomerRouteGuard({ children }: CustomerRouteGuardProps) {
  const location = useLocation();
  const { isAuthenticated, isLoading, user } = useAuth();

  if (isLoading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <div className="spinner" aria-label="Loading..." />
      </div>
    );
  }

  if (!isAuthenticated || !user) {
    return <Navigate to="/login" state={{ from: location.pathname + location.search }} replace />;
  }

  const effectiveRole = getEffectiveRole(user.roles || []);

  if (effectiveRole === 'CUSTOMER' || effectiveRole === 'ADMIN' || effectiveRole === 'SUPER_ADMIN') {
    return <>{children}</>;
  }

  // Effective role is a non-admin staff role: admit only when view-as-customer
  // toggle is on.
  if (isViewingAsCustomer()) {
    return <>{children}</>;
  }

  return <Navigate to="/dashboard" replace />;
}

export default CustomerRouteGuard;
